// @ts-strict-ignore
import { BaseSingleConnectionConnectProcessor } from './base-single-connection-connect.processor';
import { WireBridgeService } from '../../../manager/wire-bridge.service';
import { ArticleVO } from '@article/model/vo/article.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import { ConnectArticlePrepareData } from '../insert/connect-article-prepare-data';
import { IntRangeVO } from '../../../model/vo/property.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleContainerPropertyDef } from '@article/constants/article-container-property-def';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { Matrix4, Vector3 } from 'three';
import { CompositionContainer } from '@3d-scene/model3d/composition-container';
import { Injectable } from '@angular/core';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ArticleManager } from '@article/manager/article.manager';
import { CompositeArticleCacheManager } from '@article/manager/composite-article-cache.manager';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ProjectModel } from '@project/model/project.model';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';
import { StringUtils } from '@shared/util/string-utils';

@Injectable({ providedIn: 'root' })
export class WireBridgeArticleConnectProcessor extends BaseSingleConnectionConnectProcessor {
  constructor(
    projectModel: ProjectModel,
    projectModelService: ProjectModelService,
    projectArticleService: ProjectArticleService,
    protected projectMetaElementService: ProjectMetaElementService,
    articleContainerLoader: ArticleContainerLoader,
    compositeArticleCacheManager: CompositeArticleCacheManager,
    articleContainerFactory: ArticleContainerFactory,
    articleManager: ArticleManager,
    compositeArticleUtils: CompositeArticleUtils,
    compositeArticleInfoUtils: CompositeArticleInfoUtils,
    articleContainerUtils: ArticleContainerUtils,
    detachArticleUsageService: DetachArticleUsageService,
    private wireBridgeService: WireBridgeService,
    private localeResourceManager: LocaleResourceManager,
    containerService: ContainerService,
  ) {
    super(
      projectModel,
      projectModelService,
      projectArticleService,
      articleContainerLoader,
      compositeArticleCacheManager,
      articleContainerFactory,
      articleManager,
      compositeArticleUtils,
      compositeArticleInfoUtils,
      articleContainerUtils,
      detachArticleUsageService,
      containerService,
    );
  }

  protected getGroupId(article: ArticleVO): string {
    return this.isMultipliableArticle(article) ? StringUtils.createUuid() : null;
  }

  private isMultipliableArticle(article: ArticleVO): boolean {
    return PropertyUtils.getBooleanPropertyValue(article.connectionFeatures, ArticleConnectionPropertyDef.MULTIPLIABLE);
  }

  protected internalPreProcess(article: ArticleVO, connectPrepareData: ConnectArticlePrepareData, handler: Function): void {
    const multiplyRange: IntRangeVO = ArticleUtils.getWireBridgeMultiplyRange(article);
    const connectionCubesToValidate: Connection[] = connectPrepareData.acceptingConnections;
    if (connectionCubesToValidate.length > multiplyRange.right || connectionCubesToValidate.length < multiplyRange.left) {
      throw new Error(
        this.localeResourceManager.getValueByKey('connectProcessor.message.error_connect_wire_bridge', {
          '0': multiplyRange.left,
          '1': multiplyRange.right,
        })
      );
    }
    super.internalPreProcess(article, connectPrepareData, handler);
  }

  public process(
    containers: ArticleContainer[],
    baseContainer: ArticleContainer,
    givingConnectionAreas: ConnectionAreaVO[],
    rotationAngle: number
  ): void {
    const givingConnection: ConnectionAreaVO = givingConnectionAreas[0];
    const acceptingConnectionCubes: Connection[] = baseContainer.properties.getValue(ArticleContainerPropertyDef.ACCEPTING_CONNECTIONS);
    if (acceptingConnectionCubes && acceptingConnectionCubes.length > 0) {
      // get connector cube for giving area
      const givingCube: Connection = baseContainer.findFirstConnectionByName(givingConnection.name);
      const acceptingCube: Connection = acceptingConnectionCubes[0];

      if (givingCube && givingCube.compositionRectangle && acceptingCube && acceptingCube.compositionRectangle) {
        const givingRectangle: Rectangle3D = givingCube.compositionRectangle;
        const acceptingRectangle: Rectangle3D = acceptingCube.compositionRectangle;

        const acceptingContainer: ArticleContainer = this.containerService.getArticleContainer(acceptingCube);
        const parentGivingCube: Connection = ConnectionInfoUtils.getFirstGivingConnection(acceptingContainer);
        const transform: Matrix4 = MathUtils.getRotationMatrix(parentGivingCube.compositionRectangle.toFace(), givingRectangle.toFace(), false);
        baseContainer.matrix.copy(new Matrix4());
        baseContainer.applyMatrix4(transform);

        const rotMatrix: Matrix4 = MathUtils.getRotationMatrix(givingRectangle.toFace(), acceptingRectangle.toFace());

        let givingMiddlePoint: Vector3 = MathUtils.calculateMiddlePointByRectangle(givingRectangle);
        givingMiddlePoint = MathUtils.rotate([givingMiddlePoint], rotMatrix)[0];
        givingMiddlePoint = MathUtils.calculateMiddlePointByRectangle(acceptingRectangle).clone().sub(givingMiddlePoint);
        baseContainer.moveTo(givingMiddlePoint.x, givingMiddlePoint.y, givingMiddlePoint.z);

        this.attachContainer(baseContainer, givingCube, acceptingCube);
      } else {
        throw new Error(this.localeResourceManager.getValueByKey('connectProcessor.message.unknown_connection', [givingConnection.name]));
      }
    } else {
      throw new Error(this.localeResourceManager.getValueByKey('connectProcessor.message.no_connection'));
    }
  }

  public postProcess(containers: ArticleContainer[]): void {
    this.wireBridgeService.createJumpersBetweenArticles(containers);
  }

  private attachContainer(givingArticleContainer: ArticleContainer, givingCube: Connection, acceptingCube: Connection): void {
    this.projectMetaElementService.createUsageBetweenConnections(givingCube, acceptingCube);

    const acceptingArticleContainer: ArticleContainer = this.containerService.getArticleContainer(acceptingCube);
    const compositionContainer: CompositionContainer = this.containerService.getCompositionContainer(acceptingArticleContainer);

    if (compositionContainer && acceptingCube.metaElementUsage && acceptingCube.metaElementUsage.identity) {
      this.finalizeAttachContainer(compositionContainer.compositionGuid, givingArticleContainer, [acceptingCube.metaElementUsage.identity]);
    }
  }
}
